import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, CircularProgress, Menu, MenuItem, Typography } from '@mui/material';
import { useMutation } from '@apollo/client';
import React, { useState } from 'react';

import { UPDATE_JOB_APPLICATION_STATUS } from '@gql/jobs';
import {
    JobApplicationStatus,
    JobApplicationSubStatus,
    jobApplicationStatusLabels,
    jobApplicationSubStatusLabels,
    jobApplicationStatuses,
} from '@modules/jobApplications/statuses';
import { getStatusColors } from '@modules/jobApplications/utils';

interface ChangeJobApplicationStatusButtonProps {
    jobApplicationId: string;
    status?: string;
    subStatus?: string;
    onStatusChanged?: () => void;
}

// except Rejected
const ALL_STATUSES = Object.values(JobApplicationStatus).filter(s => s !== JobApplicationStatus.Rejected);

const REJECTION_SUB_STATUSES = new Set([
    JobApplicationSubStatus.NewRejected,
    JobApplicationSubStatus.InterviewRejected,
    JobApplicationSubStatus.TrialRejected,
    JobApplicationSubStatus.OfferRejected,
]);

const ChangeJobApplicationStatusButton: React.FC<ChangeJobApplicationStatusButtonProps> = ({
    jobApplicationId,
    status,
    subStatus,
    onStatusChanged,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

    const [updateJobApplicationStatus, { loading }] = useMutation(UPDATE_JOB_APPLICATION_STATUS);

    const colors = getStatusColors(status, subStatus);
    const isOpen = Boolean(anchorEl);

    const isRejected =
        status === JobApplicationStatus.Rejected ||
        REJECTION_SUB_STATUSES.has((subStatus ?? '') as JobApplicationSubStatus);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedStatus(null);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedStatus(null);
    };

    const handleStatusClick = (newStatus: string) => {
        const subs = jobApplicationStatuses[newStatus as keyof typeof jobApplicationStatuses] ?? [];
        if (subs.length === 0) {
            handleSubmit(newStatus, '');
        } else {
            setSelectedStatus(newStatus);
        }
    };

    const handleSubStatusClick = (newSubStatus: string) => {
        if (selectedStatus) {
            handleSubmit(selectedStatus, newSubStatus);
        }
    };

    const handleSubmit = async (newStatus: string, newSubStatus: string) => {
        handleClose();
        try {
            await updateJobApplicationStatus({
                variables: {
                    _id: jobApplicationId,
                    status: newStatus,
                    subStatus: newSubStatus,
                },
            });
            if (onStatusChanged) onStatusChanged();
        } catch (err) {
            console.error('Failed to update job application status', err);
        }
    };

    const subStatuses = selectedStatus
        ? jobApplicationStatuses[selectedStatus as keyof typeof jobApplicationStatuses] ?? []
        : [];

    const selectedStatusColors = getStatusColors(selectedStatus);

    return (
        <>
            <Button
                onClick={handleOpen}
                startIcon={loading ? <CircularProgress size={12} color="inherit" /> : undefined}
                disabled={loading || isRejected}
                sx={{
                    width: '100%',
                    py: '10px',
                    px: '24px',
                    borderRadius: '32px',
                    minWidth: 0,
                    textTransform: 'none',
                    backgroundColor: colors.background,
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '18px',
                    gap: '2px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '&:hover': {
                        backgroundColor: colors.background,
                        opacity: 0.85,
                    },
                    '& .MuiButton-endIcon': {
                        marginLeft: '2px',
                    },
                    '&.Mui-disabled': {
                        opacity: 0.6,
                        color: '#fff',
                        backgroundColor: colors.background,
                    },
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                    }}>
                    {colors.label}
                    {subStatus && (
                        <Box component="span" sx={{ fontSize: '12px', fontWeight: 400 }}>
                            {colors.subLabel}
                        </Box>
                    )}
                </Box>
                {!isRejected && <ChevronRightIcon sx={{ fontSize: 18 }} />}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleClose}
                onClick={e => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            minWidth: 220,
                            borderRadius: '12px',
                            border: theme => `1px solid ${theme.palette.border.main}`,
                            backgroundColor: theme => theme.palette.background.paper,
                            boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
                        },
                    },
                }}>
                {!selectedStatus ? (
                    ALL_STATUSES.map(s => {
                        const sColors = getStatusColors(s);
                        const subs = jobApplicationStatuses[s as keyof typeof jobApplicationStatuses] ?? [];
                        return (
                            <MenuItem
                                key={s}
                                onClick={() => handleStatusClick(s)}
                                sx={{ py: '8px', px: '14px', gap: '10px' }}>
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        backgroundColor: sColors.background,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                    {jobApplicationStatusLabels[s as keyof typeof jobApplicationStatusLabels]}
                                </Typography>
                                {subs.length > 0 && <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                            </MenuItem>
                        );
                    })
                ) : (
                    <>
                        <MenuItem
                            onClick={() => setSelectedStatus(null)}
                            sx={{
                                py: '8px',
                                px: '14px',
                                gap: '8px',
                                borderBottom: theme => `1px solid ${theme.palette.divider}`,
                            }}>
                            <ArrowBackIcon sx={{ fontSize: 16 }} />
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: selectedStatusColors.background,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography variant="body2" fontWeight={600}>
                                {jobApplicationStatusLabels[selectedStatus as keyof typeof jobApplicationStatusLabels]}
                            </Typography>
                        </MenuItem>
                        {subStatuses.map(sub => (
                            <MenuItem
                                key={sub}
                                onClick={() => handleSubStatusClick(sub)}
                                sx={{ py: '8px', px: '14px' }}>
                                <Typography
                                    variant="body2"
                                    sx={
                                        REJECTION_SUB_STATUSES.has(sub as JobApplicationSubStatus)
                                            ? { color: 'error.main' }
                                            : undefined
                                    }>
                                    {jobApplicationSubStatusLabels[sub as keyof typeof jobApplicationSubStatusLabels]}
                                </Typography>
                            </MenuItem>
                        ))}
                    </>
                )}
            </Menu>
        </>
    );
};

export default ChangeJobApplicationStatusButton;
